import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Switch,
  Typography,
} from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { useTranslation } from 'react-i18next';
import styles from './EditNoteDialog.module.scss';
import classNames from 'classnames/bind';
import MenuOption from '~/components/jobs/menu-option-file/MenuOption';
import { BaseRichEditor, BaseSendEmail } from '~/components/common';
import { IEditNote } from '~/utils/interface/note';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleCreateNote, handleUpdateNotes } from '~/thunks/customer/customerThunk';
import { customerActions } from '~/thunks/customer/customerSlice';
import { handleGetCustomerFiles } from '~/thunks/customer/customerThunk';

const cx = classNames.bind(styles);

type EditNoteDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  mode?: string;
  data?: string;
  id?: string;
};

const initialValues: IEditNote = {
  note: '',
  jobId: undefined,
  type: '',
  files: undefined,
  isSendMail: false,
  to: '',
  subject: '',
  customerFileIds: [],
  cc: '',
  bcc: '',
  from: '',
};

const EditNoteDialog = (props: EditNoteDialogProps) => {
  //#region Destructuring Props
  const { mode } = props;
  // //#endregion Destructuring Props
  // //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/job', 'admin/common']);
  const { needRefreshData, menuSelectedId } = useAppSelector((state) => state.customer);
  //#endregion Declare Hook
  useAppSelector((state) => state.customer);
  //#region Selector
  //#endregion Selector
  //#region Declare State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [checked, setChecked] = useState(false);
  const [dateSendEmail, setDataSendEmail] = useState<IEditNote>();
  const [checkedCRM, setCheckedCRM] = useState(false);
  //#endregion Declare State
  //#region Implement Hook
  useEffect(() => {
    if (mode === DIALOG_MODE_CREATE) {
      form.setFieldValue('type', 'customers');
      form.setFieldValue('customerId', cast(props.id));

      if (checked) {
        form.setFieldValue('isSendMail', true);
      }

      form.setFieldValue('note', cast(dateSendEmail?.note));
      form.setFieldValue('to', cast(dateSendEmail?.to));
      form.setFieldValue('subject', cast(dateSendEmail?.subject));
      form.setFieldValue('cc', cast(dateSendEmail?.cc));
      form.setFieldValue('bcc', cast(dateSendEmail?.bcc));
      form.setFieldValue('from', cast(dateSendEmail?.from));
    }

    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('note', props.data);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, props.id, props.data, needRefreshData, dispatch, dateSendEmail]);

  useEffect(() => {
    if (checked) {
      form.setFieldValue('type', 'customers');
      form.setFieldValue('customerId', cast(props.id));

      form.setFieldValue('isSendMail', true);
      form.setFieldValue('note', cast(dateSendEmail?.note));
      form.setFieldValue('to', cast(dateSendEmail?.to));
      form.setFieldValue('subject', cast(dateSendEmail?.subject));
      form.setFieldValue('cc', cast(dateSendEmail?.cc));
      form.setFieldValue('bcc', cast(dateSendEmail?.bcc));
      form.setFieldValue('from', cast(dateSendEmail?.from));
    } else {
      form.setFieldValue('type', 'customers');
      form.setFieldValue('customerId', cast(props.id));

      form.setFieldValue('isSendMail', false);
      form.setFieldValue('note', '');
      form.setFieldValue('to', '');
      form.setFieldValue('subject', '');
      form.setFieldValue('cc', '');
      form.setFieldValue('bcc', '');
      form.setFieldValue('from', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);
  const handleSwitchToggle = () => {
    const newValue = !checkedCRM;
    setCheckedCRM(newValue);
    form.setFieldValue('isCrm', newValue);
  };
  useEffect(() => {
    dispatch(handleGetCustomerFiles({ id: props.id }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, props.id]);

  useEffect(() => {
    if (mode === DIALOG_MODE_CREATE) {
      form.setFieldValue('isCrm', true);
      setCheckedCRM(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  //#endregion Implement Hook
  // //#region Handle Function
  const handleCancel = () => {
    handleSetOpen(false);
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateNote(values)).unwrap();
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          dispatch(customerActions.changeNeedRefreshData(true));
          handleSetOpen(false);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        const { note } = values;
        try {
          await dispatch(handleUpdateNotes({ id: Number(menuSelectedId), params: { note } })).unwrap();
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          dispatch(customerActions.changeNeedRefreshData(true));
          handleSetOpen(false);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (mode === DIALOG_MODE_CREATE) {
      form.resetForm();
    }
    if (mode === DIALOG_MODE_EDIT) {
      form.resetForm();
    }
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
      setChecked(false);
      setCheckedCRM(false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onChangeValue = (value: string) => {
    form.setFieldValue('note', value);
  };

  //#endregion Handle Function

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('activitiesNotes.addNewNote') : t('activitiesNotes.editNote')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box className={cx('note')}>
          {mode === DIALOG_MODE_CREATE ? (
            <Box className={cx('header')} display='flex' justifyContent='space-between' alignItems='center'>
              <Box display='flex' alignItems='center'>
                <Typography className='mr-[10px]'>{t('email.sendEmail', { ns: 'admin/component' })}</Typography>
                <Switch
                  onClick={() => {
                    setChecked(!checked);
                  }}
                />
              </Box>
              <Box display='flex' alignItems='center'>
                <Typography className='mr-[10px]'>{t('crmActivity')}</Typography>
                <Switch checked={checkedCRM} onClick={handleSwitchToggle} />
              </Box>
            </Box>
          ) : (
            <></>
          )}

          {checked ? (
            <BaseSendEmail
              onChange={(value) => {
                setDataSendEmail(value);
              }}
              formik={form}
              fileType='customer'
              customerId={props.id}
            />
          ) : (
            <>
              <InputLabel className='mb-[20px]'>{t('activitiesNotes.notes')}</InputLabel>
              <BaseRichEditor
                onCreateNew={onChangeValue}
                valueProps={form.values.note}
                isError={Boolean(form.errors?.note)}
                errorMessage={form.errors.note}
              />
            </>
          )}
        </Box>

        <MenuOption anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} handleDeleteItem={() => {}} />
      </DialogContent>

      <DialogActions className='justify-end'>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditNoteDialog);
